// src/pages/projects/fitness/components/linechart.tsx
import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface LiftSet {
  reps: number;
  weight: number;
  rpe: number;
}

interface RunSet {
  distance: number;
  pace: number;
  time: number;
}

type WorkoutSet = LiftSet | RunSet;

interface WorkoutExercise {
  name: string;
  mode: "lift" | "run";
  sets: WorkoutSet[];
}

interface WorkoutLog {
  createdAt: { seconds: number };
  exercises: WorkoutExercise[];
}

interface Props {
  logs: WorkoutLog[];
  selectedExercise: string;
  mode: "lift" | "run";
}

const LineChartComponent: React.FC<Props> = ({
  logs,
  selectedExercise,
  mode,
}) => {
  const data = logs
    .filter((log) =>
      log.exercises.some(
        (ex) => ex.name === selectedExercise && ex.mode === mode
      )
    )
    .map((log) => {
      const date = new Date(log.createdAt.seconds * 1000);
      const dateLabel = date.toLocaleDateString();
      const exercise = log.exercises.find((ex) => ex.name === selectedExercise);

      if (!exercise) return null;

      if (mode === "lift") {
        const liftSets = exercise.sets as LiftSet[];
        const topSet = liftSets.reduce((a, b) => (a.weight > b.weight ? a : b));
        return {
          date: dateLabel,
          timestamp: date.getTime(),
          weight: topSet.weight,
          reps: topSet.reps,
        };
      } else {
        const runSets = exercise.sets as RunSet[];
        const longestRun = runSets.reduce((a, b) =>
          a.distance > b.distance ? a : b
        );
        return {
          date: dateLabel,
          timestamp: date.getTime(),
          "Distance (mi)": longestRun.distance,
        };
      }
    })
    .filter(Boolean)
    .sort((a, b) => a!.timestamp - b!.timestamp);

  return (
    <div style={{ width: "100%", height: 300, marginBottom: 40 }}>
      <ResponsiveContainer>
        <LineChart
          data={data as any[]}
          margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip
            formatter={(value: any, name: any, props: any) => {
              if (mode === "lift") {
                const { reps } = props.payload;
                return [`${value} lbs, ${reps} reps`, "Top Set"];
              } else {
                return [`${value} mi`, name];
              }
            }}
          />
          <Line
            type="monotone"
            dataKey={mode === "lift" ? "weight" : "Distance (mi)"}
            stroke="#8884d8"
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LineChartComponent;
